import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { CatalogProductUpdateInput } from "@/modules/catalog/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import {
  useCatalogProductQuery,
  useUpdateCatalogProduct,
} from "../api/queries";
import { CatalogProductForm } from "../ui/CatalogProductForm";

export function CatalogProductEditPage() {
  const { id } = useParams({ from: "/app/catalog/$id/edit" });
  const nav = useNavigate({ from: "/app/catalog/$id/edit" });
  const productQuery = useCatalogProductQuery(id ?? "");
  const updateProduct = useUpdateCatalogProduct(id ?? "");

  const product = productQuery.data;
  const isLoading = productQuery.isLoading;
  const isError = productQuery.isError || !id;

  if (isError && !productQuery.isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "Catalog", to: "/app/catalog" },
              { label: "ແກ້ໄຂສິນຄ້າ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive">ບໍ່ພົບສິນຄ້າ ຫຼື ເກີດຂໍ້ຜິດພາດ</p>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() => nav({ to: "/app/catalog" })}
          >
            ກັບຄືນຫນ້າລາຍການ
          </Button>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Catalog", to: "/app/catalog" },
            {
              label: product ? `ແກ້ໄຂ: ${product.name}` : "ແກ້ໄຂສິນຄ້າ",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mx-auto max-w-5xl">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-bold text-2xl tracking-tight">ແກ້ໄຂສິນຄ້າ</h1>
              <p className="text-muted-foreground text-sm">
                {product ? `ແກ້ໄຂຂໍ້ມູນ: ${product.name}` : "ກຳລັງໂຫຼດ..."}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/catalog" })}
            >
              <ArrowLeftIcon className="size-4" aria-hidden />
              ກັບຄືນ
            </Button>
          </div>

          {isLoading ? (
            <p className="py-8 text-center text-muted-foreground">
              ກຳລັງໂຫຼດຂໍ້ມູນ...
            </p>
          ) : product ? (
            <CatalogProductForm
              key={product.id}
              initialValues={{
                name: product.name,
                type: product.type as
                  | "part_new"
                  | "part_oem"
                  | "part_used"
                  | "service",
                categoryId: product.categoryId ?? undefined,
                brandId: product.brandId ?? undefined,
                description: product.description ?? "",
                status: product.status as
                  | "draft"
                  | "active"
                  | "hidden"
                  | "archived",
              }}
              onSubmit={async (vals) => {
                try {
                  await updateProduct.mutateAsync(
                    vals as CatalogProductUpdateInput,
                  );
                  toast.success("ແກ້ໄຂສິນຄ້າສຳເລັດ");
                  nav({ to: "/app/catalog" });
                } catch {
                  toast.error("ແກ້ໄຂລົ້ມເຫຼວ");
                }
              }}
              onCancel={() => nav({ to: "/app/catalog" })}
              submitting={updateProduct.isPending}
              submitLabel="ບັນທຶກການປ່ຽນແປງ"
            />
          ) : null}
        </div>
      </Main>
    </>
  );
}
